import { Component, OnInit, ViewChild, TemplateRef, AfterViewInit, HostListener, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HeaderComponent } from '../../shared/components/header/header.component';
import { AdminSidebarComponent } from '../sidebar/admin-sidebar.component';
import { PageHeaderComponent } from '../../shared/components/page-header/page-header.component';
import { TableComponent, TableColumn, PaginationConfig, ModalComponent, withCellTemplates } from '../../shared/components/primeng';
import { FilterPanelComponent, FilterField } from '../../shared/components/filter-panel/filter-panel.component';
import { ConfirmationDialogComponent } from '../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { DataProviderEntityFormComponent, DataProviderEntityFormData } from '../../shared/components/data-provider-entity-form/data-provider-entity-form.component';
import { AuthApiService } from '@angular-workspace/services';
import { FormatService } from '../../shared/services/format.service';
import { ToasterService } from '../../services/toaster.service';
import { KeycloakService } from 'keycloak-angular';

export interface NewRequestData {
  dataProvider: string;
  type: string;
  userName: string;
  jobTitle: string;
  requestStatus: string;
  createdDate: string;
  action?: string;
  originalData?: any;
}

export interface RequestFilters {
  entityType: Array<{ label: string; value: string }> | string[];
}

@Component({
  selector: 'app-admin-new-requests',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    HeaderComponent,
    AdminSidebarComponent,
    PageHeaderComponent,
    TableComponent,
    FilterPanelComponent,
    ConfirmationDialogComponent,
    ModalComponent,
    DataProviderEntityFormComponent,
  ],
  templateUrl: './new-requests.component.html',
  styleUrl: './new-requests.component.scss',
})
export class AdminNewRequestsComponent implements OnInit, AfterViewInit {
  @ViewChild('actionCell', { static: false }) actionCellTemplate!: TemplateRef<any>;
  @ViewChild('statusCell', { static: false }) statusCellTemplate!: TemplateRef<any>;
  @ViewChild('viewModalHeaderTemplate', { static: false }) viewModalHeaderTemplate!: TemplateRef<any>;

  constructor(
    private authApiService: AuthApiService,
    private formatService: FormatService,
    private toasterService: ToasterService,
    private keycloak: KeycloakService,
    private cdr: ChangeDetectorRef
  ) {}

  // Form data for data-provider-entity-form component
  viewFormData: DataProviderEntityFormData = {};

  // Filter fields configuration
  filterFields: FilterField[] = [
    {
      key: 'entityType',
      label: 'Entity Type',
      type: 'multiselect',
      // Keep labels consistent with Consumer "Create Organisation" entity type dropdown.
      // Organisation requests list passes entityType like MahaAgX (display label for the query param).
      options: [
        { label: 'Private Limited Company', value: 'PRIVATE_LIMITED' },
        { label: 'Public Limited Company', value: 'PUBLIC_LIMITED' },
        { label: 'Partnership Firm', value: 'PARTNERSHIP' },
        { label: 'Sole Proprietorship', value: 'SOLE_PROPRIETOR' },
        { label: 'Limited Liability Partnership', value: 'LLP' },
        { label: 'Government Organization', value: 'GOVERNMENT' },
        { label: 'Non-Profit Organization', value: 'NON_PROFIT' },
      ],
      value: [],
      maxSelected: 1
    }
  ];

  openDropdownRowIndex: number | null = null;
  isApproveConfirmationVisible = false;
  isRejectConfirmationVisible = false;
  requestToApprove: NewRequestData | null = null;
  requestToReject: NewRequestData | null = null;
  isViewModalVisible = false;
  selectedRequest: NewRequestData | null = null;
  requestDetails: any = null;

  columns: TableColumn[] = [
    {
      key: 'dataProvider',
      label: 'Organisation Name',
      sortable: false,
      width: '20%'
    },
    {
      key: 'type',
      label: 'Type',
      sortable: true,
      width: '15%'
    },
    {
      key: 'userName',
      label: 'User Name',
      sortable: true,
      width: '15%'
    },
    {
      key: 'jobTitle',
      label: 'Job Title',
      sortable: true,
      width: '15%'
    },
    {
      key: 'requestStatus',
      label: 'Request Status',
      sortable: true,
      width: '15%'
    },
    {
      key: 'createdDate',
      label: 'Created Date',
      sortable: true,
      width: '12%'
    },
    {
      key: 'action',
      label: 'Action',
      sortable: false,
      width: '120px',
      align: 'left',
      fixed: 'right'
    }
  ];

  allRequestsData: NewRequestData[] = [];
  requestsData: NewRequestData[] = [];
  currentFilters: { entityType?: any } = {};

  pagination: PaginationConfig = {
    enabled: true,
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    pageSizeOptions: [10, 25, 50, 100],
    serverSide: true
  };

  loading = false;
  emptyMessage = 'No requests found';

  /** Current sort state for server-side sorting */
  sortColumn: string | null = null;
  sortDirection: 'asc' | 'desc' = 'asc';

  async ngOnInit(): Promise<void> {
    await this.waitForKeycloakReady();
    this.loadRequests();
  }

  private async waitForKeycloakReady(): Promise<void> {
    try {
      const keycloakInstance = this.keycloak.getKeycloakInstance();
      if (keycloakInstance?.authenticated && keycloakInstance?.token) return;
      const maxWaitTime = 5000, checkInterval = 100, startTime = Date.now();
      while (Date.now() - startTime < maxWaitTime) {
        const instance = this.keycloak.getKeycloakInstance();
        if (instance?.authenticated && instance?.token) return;
        await new Promise(resolve => setTimeout(resolve, checkInterval));
      }
    } catch (error) {
      console.error('Error waiting for Keycloak:', error);
    }
  }

  async loadRequests(): Promise<void> {
    this.loading = true;
    this.openDropdownRowIndex = null;
    this.dropdownPosition = null;
    try {
      // Backend expects repeated `entityType` query params (display labels). With no selection, send all types (matches control-plane usage).
      let entityTypesForApi: string[] | undefined;
      const rawEntityFilter = this.currentFilters.entityType;
      const hasEntityTypeSelection =
        rawEntityFilter !== undefined &&
        rawEntityFilter !== null &&
        (Array.isArray(rawEntityFilter)
          ? rawEntityFilter.length > 0
          : typeof rawEntityFilter === 'string'
            ? rawEntityFilter.trim().length > 0
            : false);

      if (hasEntityTypeSelection) {
        let selected: string | undefined;
        if (Array.isArray(rawEntityFilter) && rawEntityFilter.length > 0) {
          const first = rawEntityFilter[0];
          selected = typeof first === 'string' ? first : (first?.label ?? first?.value);
        } else if (typeof rawEntityFilter === 'string') {
          selected = rawEntityFilter;
        }
        entityTypesForApi = selected ? [selected] : undefined;
      } else {
        const opts = this.filterFields.find((f) => f.key === 'entityType')?.options ?? [];
        entityTypesForApi = opts
          .map((o: { label?: string; value?: string } | string) =>
            typeof o === 'string' ? o : (o.label ?? o.value ?? '').trim()
          )
          .filter(Boolean);
        if (entityTypesForApi.length === 0) entityTypesForApi = undefined;
      }

      const sortString = this.getSortString();
      const response = await this.authApiService.getOrganisationRequests(
        this.pagination.currentPage,
        this.pagination.pageSize,
        sortString,
        entityTypesForApi,
        'pending' // Only show pending requests
      );

      let mappedData: NewRequestData[] = [];

      // Handle API response structure: { result: [...], paginationInfo: {...} }
      if (response && response['result'] && Array.isArray(response['result'])) {
        const dataArray = response['result'];
        
        mappedData = dataArray.map((item: any) => {
          // Map API response fields to component data structure
          // Based on typical organisation request structure:
          // - organisation name (dataProvider)
          // - organisation type (type)
          // - user information (userName, jobTitle)
          // - status (requestStatus)
          // - createdAt (createdDate)
          
          const dataProvider = item.organisationName || item.organisation_name || item.name || item.orgName || 'N/A';
          const type = item.organisationType || item.organisation_type || item.type || item.entityType || 'N/A';
          const userName = item.userName || item.user_name || item.name || item.requestedBy || 'N/A';
          const jobTitle = item.jobTitle || item.job_title || item.position || 'N/A';
          const requestStatus = this.formatService.toPascalCase(
            item.status || item.requestStatus || item.request_status || 'Pending'
          );
          
          // Format date
          let createdDate = 'N/A';
          const requestDate = item.createdAt || item.created_at || item.createdDate || item.created_date;
          if (requestDate) {
            createdDate = this.formatService.formatDate(requestDate);
          }

          return {
            dataProvider,
            type,
            userName,
            jobTitle,
            requestStatus,
            createdDate,
            action: 'Action',
            originalData: item // Store original API response data
          };
        });

        // Update pagination from paginationInfo
        if (response['paginationInfo'] && response['paginationInfo']['totalCount'] !== undefined) {
          this.pagination = {
            ...this.pagination,
            totalItems: response['paginationInfo']['totalCount']
          };
        }
      } else if (response && response['paginationInfo']) {
        // Handle case where response has paginationInfo but no result array
        if (response['paginationInfo']['totalCount'] !== undefined) {
          this.pagination = {
            ...this.pagination,
            totalItems: response['paginationInfo']['totalCount']
          };
        }
        mappedData = []; // No data to map
      }

      this.requestsData = mappedData;
      this.allRequestsData = [...mappedData];

      if (mappedData.length === 0) {
        this.emptyMessage = 'No requests found';
      }
    } catch (error) {
      console.error('Error loading organisation requests:', error);
      this.requestsData = [];
      this.allRequestsData = [];
      this.pagination = {
        ...this.pagination,
        totalItems: 0
      };
      this.emptyMessage = 'No requests found';
    } finally {
      this.loading = false;
    }
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = date.toLocaleString('en-US', { month: 'short' });
      const year = date.getFullYear();
      
      return `${day}-${month}-${year}`;
    } catch (error) {
      return dateString;
    }
  }

  onPageChange(page: number): void {
    this.pagination = {
      ...this.pagination,
      currentPage: page
    };
    this.loadRequests();
  }

  onPageSizeChange(pageSize: number): void {
    this.pagination = {
      ...this.pagination,
      pageSize: pageSize,
      currentPage: 1
    };
    this.loadRequests();
  }

  onSortChange(event: { column: string; direction: 'asc' | 'desc' }): void {
    this.sortColumn = event.column;
    this.sortDirection = event.direction;
    this.pagination = { ...this.pagination, currentPage: 1 };
    this.loadRequests();
  }

  /**
   * Map UI column key to API sort field string
   */
  getSortString(): string {
    const dir = this.sortColumn ? this.sortDirection : 'desc';
    const apiFieldMap: Record<string, string> = {
      type: 'entityType',
      userName: 'userName',
      jobTitle: 'jobTitle',
      requestStatus: 'status',
      createdDate: 'createdAt',
    };
    const apiField = (this.sortColumn && apiFieldMap[this.sortColumn]) || 'createdAt';
    return `${apiField}:${dir}`;
  }

  ngAfterViewInit(): void {
    // Use withCellTemplates for immutable update - ensures table re-renders with templates (fixes dropdown on first load)
    const updates: { key: string; cellTemplate: TemplateRef<any> }[] = [];
    if (this.actionCellTemplate) updates.push({ key: 'action', cellTemplate: this.actionCellTemplate });
    if (this.statusCellTemplate) updates.push({ key: 'requestStatus', cellTemplate: this.statusCellTemplate });
    if (updates.length) {
      this.columns = withCellTemplates(this.columns, updates);
      this.cdr.detectChanges();
    }
  }
  
  getStatusChipClass(status: string): string {
    if (!status) return 'status-chip status-chip-default';
    
    const statusLower = status.toLowerCase();
    if (statusLower === 'pending' || statusLower === 'pending approval') {
      return 'status-chip status-chip-pending';
    } else if (statusLower === 'granted' || statusLower === 'approved') {
      return 'status-chip status-chip-granted';
    } else if (statusLower === 'rejected') {
      return 'status-chip status-chip-rejected';
    }
    
    return 'status-chip status-chip-default';
  }

  toggleActionDropdown(event: Event, row: NewRequestData, rowIndex: number): void {
    event.stopPropagation();
    if (this.openDropdownRowIndex === rowIndex) {
      this.openDropdownRowIndex = null;
      this.dropdownPosition = null;
      this.cdr.detectChanges();
      return;
    }
    const btn = event.currentTarget as HTMLElement;
    const rect = btn.getBoundingClientRect();
    this.dropdownPosition = { top: rect.bottom + 4, left: rect.left, width: rect.width };
    this.openDropdownRowIndex = rowIndex;
    this.cdr.detectChanges();
  }

  closeActionDropdown(): void {
    this.openDropdownRowIndex = null;
    this.dropdownPosition = null;
    this.cdr.detectChanges();
  }

  isDropdownOpen(rowIndex: number): boolean {
    return this.openDropdownRowIndex === rowIndex;
  }

  /** Row whose action dropdown is open; used by the fixed overlay. */
  get openDropdownRow(): NewRequestData | null {
    if (this.openDropdownRowIndex == null || this.openDropdownRowIndex < 0 || this.openDropdownRowIndex >= this.requestsData.length) {
      return null;
    }
    return this.requestsData[this.openDropdownRowIndex] ?? null;
  }

  /** Position for the fixed dropdown overlay. */
  dropdownPosition: { top: number; left: number; width: number } | null = null;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (this.openDropdownRowIndex !== null &&
        !target.closest('.action-cell-wrapper') &&
        !target.closest('.action-dropdown-overlay')) {
      this.closeActionDropdown();
    }
  }

  isRequestProcessed(row: NewRequestData): boolean {
    // Check if request status is "Granted" or "Rejected" (case-insensitive)
    const status = (row.requestStatus || '').toLowerCase();
    return status === 'granted' || status === 'rejected';
  }

  handleAction(action: string, row: NewRequestData): void {
    this.closeActionDropdown();
    
    // Don't proceed if request is already processed
    if (this.isRequestProcessed(row) && (action === 'approve' || action === 'reject')) {
      return;
    }
    
    if (action === 'view') {
      // Store the selected request
      this.selectedRequest = row;
      this.requestDetails = row.originalData || null;
      
      // Map request data to form data
      this.mapRequestDataToFormData();
      
      // Open modal
      this.isViewModalVisible = true;
    } else if (action === 'approve') {
      // Store the request to approve and show confirmation dialog
      this.requestToApprove = row;
      this.isApproveConfirmationVisible = true;
    } else if (action === 'reject') {
      // Store the request to reject and show confirmation dialog
      this.requestToReject = row;
      this.isRejectConfirmationVisible = true;
    }
  }

  mapRequestDataToFormData(): void {
    if (!this.requestDetails) {
      console.warn('No request details available to map form data');
      this.viewFormData = {};
      return;
    }

    // Log the API response structure for debugging
    console.log('Request Details from API:', this.requestDetails);

    // Helper function to format entity type for display
    const formatEntityType = (value: string): string => {
      if (!value) return '';
      // Convert to PascalCase for display
      return value
        .split(/[_\s-]/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    };

    // Helper function to format entity sector for display
    const formatEntitySector = (value: string): string => {
      if (!value) return '';
      // Convert to PascalCase for display
      return value
        .split(/[_\s-]/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    };

    // Extract user name - could be in various formats
    const userData = this.requestDetails;
    const fullName = userData.userName || 
                     userData.user_name || 
                     userData.name || 
                     userData.fullName ||
                     userData.full_name ||
                     this.selectedRequest?.userName ||
                     '';

    const nameParts = fullName.split(' ').filter((part: string) => part.trim());
    const firstName = userData.firstName || 
                      userData.first_name || 
                      (nameParts.length > 0 ? nameParts[0] : '');
    const lastName = userData.lastName || 
                     userData.last_name || 
                     (nameParts.length > 1 ? nameParts.slice(1).join(' ') : '');

    // Extract document file names and paths
    const documentFiles: { [key: string]: string } = {};
    const documentPaths: { [key: string]: string } = {};
    
    const certPath = userData.certificatePath || userData.certificate_path || '';
    if (certPath) {
      documentFiles['incorporation'] = certPath.split('/').pop() || certPath;
      documentPaths['incorporation'] = certPath;
    }
    
    const panPath = userData.pancardPath || userData.pancard_path || '';
    if (panPath) {
      documentFiles['panCard'] = panPath.split('/').pop() || panPath;
      documentPaths['panCard'] = panPath;
    }
    
    const authPath = userData.orgDocuments || userData.org_documents || userData.letterOfAuthorization || userData.letter_of_authorization || '';
    if (authPath) {
      documentFiles['authorization'] = authPath.split('/').pop() || authPath;
      documentPaths['authorization'] = authPath;
    }
    
    const relPath = userData.relevantDocPath || userData.relevant_doc_path || '';
    if (relPath) {
      documentFiles['others'] = relPath.split('/').pop() || relPath;
      documentPaths['others'] = relPath;
    }

    // Map request data to form data structure
    this.viewFormData = {
      // User Details
      firstName: firstName,
      lastName: lastName,
      registeredEmail: userData.email || 
                       userData.registeredEmail || 
                       userData.registered_email ||
                       userData.userEmail ||
                       userData.managerEmail ||
                       '',
      jobTitle: userData.jobTitle || 
                userData.job_title || 
                userData.position ||
                this.selectedRequest?.jobTitle ||
                '',
      employeeId: userData.empId ||
                  userData.employeeId || 
                  userData.employee_id ||
                  '',
      
      // Entity Details
      entityType: formatEntityType(
        userData.entityType || 
        userData.entity_type || 
        userData.type ||
        userData.organisationType ||
        userData.organisation_type ||
        this.selectedRequest?.type ||
        ''
      ),
      entitySector: formatEntitySector(
        userData.orgSector ||
        userData.entitySector || 
        userData.entity_sector || 
        userData.sector ||
        ''
      ),
      entityName: userData.name || 
                  userData.entityName || 
                  userData.organisationName ||
                  userData.organisation_name ||
                  userData.orgName ||
                  this.selectedRequest?.dataProvider ||
                  '',
      websiteUrl: userData.websiteLink ||
                 userData.websiteUrl || 
                 userData.website_url || 
                 userData.website ||
                 userData.url ||
                 '',
      address: userData.address || 
               userData.fullAddress ||
               userData.full_address ||
               '',
      
      // Document files
      documentFiles: documentFiles,
      // Document paths for PDF viewing
      documentPaths: documentPaths,
    };
  }

  closeViewModal(): void {
    this.isViewModalVisible = false;
    this.selectedRequest = null;
    this.requestDetails = null;
    this.viewFormData = {};
  }

  /**
   * Extract display message from API success response (message, detail, etc.)
   */
  private getMessageFromApiResponse(response: any): string | null {
    if (!response || typeof response !== 'object') return null;
    const msg = response.message ?? response.detail ?? response.msg ?? response.error;
    return typeof msg === 'string' && msg.trim() ? msg : null;
  }

  /**
   * Extract display message from API error (e.g. 400 Bad Request body).
   */
  private getMessageFromApiError(error: any): string {
    const fallback = 'Something went wrong. Please try again.';
    if (!error) return fallback;
    const msg =
      error?.error?.detail ??
      error?.error?.message ??
      error?.detail ??
      error?.message;
    return typeof msg === 'string' && msg.trim() ? msg : fallback;
  }

  async onApproveConfirm(): Promise<void> {
    if (!this.requestToApprove) {
      this.isApproveConfirmationVisible = false;
      return;
    }

    try {
      // Extract request ID from originalData
      const originalData = this.requestToApprove.originalData || {};
      const requestId = originalData.id || originalData.requestId || originalData.request_id || '';
      
      if (!requestId) {
        console.error('Request ID not found in originalData:', originalData);
        // Show error message to user
        this.isApproveConfirmationVisible = false;
        this.requestToApprove = null;
        return;
      }

      // Prepare payload
      const payload = {
        req_id: requestId,
        status: 'granted'
      };

      console.log('Approving request with payload:', payload);

      // Call the approve API
      const response = await this.authApiService.approveOrganisationRequest(payload);

      // Show success toaster from API response or fallback
      const message = this.getMessageFromApiResponse(response) ?? "You've successfully approved the organisation";
      this.toasterService.success(message);

      // Reload requests after successful approval
      await this.loadRequests();

      // Close confirmation dialog
      this.isApproveConfirmationVisible = false;
      this.requestToApprove = null;
    } catch (error: any) {
      console.error('Error approving organisation request:', error);
      this.toasterService.error(this.getMessageFromApiError(error));
      // Keep the dialog open so user can try again or cancel
    }
  }

  onApproveCancel(): void {
    this.isApproveConfirmationVisible = false;
    this.requestToApprove = null;
  }

  async onRejectConfirm(): Promise<void> {
    if (!this.requestToReject) {
      this.isRejectConfirmationVisible = false;
      return;
    }

    try {
      // Extract request ID from originalData
      const originalData = this.requestToReject.originalData || {};
      const requestId = originalData.id || originalData.requestId || originalData.request_id || '';
      
      if (!requestId) {
        console.error('Request ID not found in originalData:', originalData);
        // Show error message to user
        this.isRejectConfirmationVisible = false;
        this.requestToReject = null;
        return;
      }

      // Prepare payload
      const payload = {
        req_id: requestId,
        status: 'rejected'
      };

      console.log('Rejecting request with payload:', payload);

      // Call the approve API (same method, different status)
      const response = await this.authApiService.approveOrganisationRequest(payload);

      // Show toaster from API response or fallback (error style for rejection)
      const message = this.getMessageFromApiResponse(response) ?? "You've successfully rejected the organisation";
      this.toasterService.error(message);

      // Reload requests after successful rejection
      await this.loadRequests();

      // Close confirmation dialog
      this.isRejectConfirmationVisible = false;
      this.requestToReject = null;
    } catch (error: any) {
      console.error('Error rejecting organisation request:', error);
      this.toasterService.error(this.getMessageFromApiError(error));
      // Keep the dialog open so user can try again or cancel
    }
  }

  onRejectCancel(): void {
    this.isRejectConfirmationVisible = false;
    this.requestToReject = null;
  }

  onFilterChange(filters: Record<string, any>): void {
    console.log('Filter changed:', filters);
    
    // Store current filters
    this.currentFilters = { ...filters };
    
    // Reset to first page when filters change
    this.pagination = {
      ...this.pagination,
      currentPage: 1
    };
    
    // Reload data with new filters
    this.loadRequests();
  }

  onClearFilters(): void {
    // Reset filter fields
    this.filterFields = this.filterFields.map(field => ({
      ...field,
      value: field.type === 'multiselect' ? [] : field.type === 'dateRange' ? null : ''
    }));
    
    // Clear current filters
    this.currentFilters = {};
    
    // Reload data
    this.pagination = {
      ...this.pagination,
      currentPage: 1
    };
    this.loadRequests();
  }

  /**
   * Get approve message with highlighted organisation name
   */
  getApproveMessage(): string {
    if (this.requestToApprove && this.requestToApprove.dataProvider) {
      const orgName = this.requestToApprove.dataProvider;
      return `Are you sure you want to approve the request for <strong>${orgName}</strong>? This action will approve the organisation request.`;
    }
    return 'Are you sure you want to approve this request? This action will approve the organisation request.';
  }

  /**
   * Get reject message with highlighted organisation name
   */
  getRejectMessage(): string {
    if (this.requestToReject && this.requestToReject.dataProvider) {
      const orgName = this.requestToReject.dataProvider;
      return `Are you sure you want to reject the request for <strong>${orgName}</strong>? This action will reject the organisation request.`;
    }
    return 'Are you sure you want to reject this request? This action will reject the organisation request.';
  }

  /**
   * Get modal header with organisation status
   */
  getViewModalHeader(): string {
    if (this.selectedRequest && this.selectedRequest.requestStatus) {
      return `Organisation Details - ${this.selectedRequest.requestStatus}`;
    }
    return 'Organisation Details';
  }
}

